import React from "react";
import { useDispatch } from "react-redux";
import ModalManager from "../layout/ModalManager";
import { assert } from "../../classes/utilities/ErrorHandling";
import { LayoutOrientations } from "../../globals";
import Separator from "../layout/Separator";
import Panel from "../layout/Panel";
import { TriangleIconRight } from "../Iconset";
import Spacer from "../layout/Spacer";
import { IContextMenuGroup, IContextMenuItem } from "../../interfaces/Misc";
import AnalyticsManager from "../../classes/AnalyticsManager";
import { useTypedDispatch } from "../../state/hooks";

interface ContextMenuProps {
	items?: IContextMenuItem[];
	groups?: IContextMenuGroup[];
	category?: string;
	size?: { x?: string, y?: string };
	onShutdown?: () => void;
}

ContextMenu.show = function (e: MouseEvent | React.MouseEvent, items?: IContextMenuItem[], category = "default", groups?: IContextMenuGroup[], size?: { x?: string, y?: string })
{
	if ((items && items.length) || (groups && groups.length))
	{
		return ModalManager.addModal({
			showBackdrop: false,
			clickThrough: false,
			allowClickOut: true,
			rect: {
				x: e.clientX,
				y: e.clientY,
			},
			content:
				<ContextMenu
					category={category}
					groups={groups}
					items={items}
					size={size}
				/>
		});
	}
}

export default function ContextMenu(props: ContextMenuProps)
{
	const dispatch = useTypedDispatch();

	function showNestedItems(x: number, y: number, category = "default", groups = undefined, items: IContextMenuItem[])
	{
		ModalManager.addModal({
			showBackdrop: false,
			clickThrough: false,
			allowClickOut: true,
			rect: {
				x: x,
				y: y,
			},
			content:
				<ContextMenu
					category={category}
					groups={groups}
					items={items}
					onShutdown={props.onShutdown}
				/>
		});
	}

	function generateMenuItems()
	{
		if (props.items)
			assert(Array.isArray(props.items));

		if (props.groups)
			assert(Array.isArray(props.groups));

		let groups = props.groups;
		if (!groups && props.items)
		{
			groups = [{items: [...props.items]}];
		}
		const output = new Array<JSX.Element>();

		let idx = 0;
		if ( groups )
		{
			for (const group of groups)
			{
				for (const item of group.items)
				{
					if(item.disabled) item.noclick = true;
					const generatedStyle = {
						fontSize: item.size ? (100*item.size).toString()+"%" : "100%",
						color: item.disabled ? "#737373" : "#d9d9d9",
					}
					output.push(
						<div className={item.noclick ? "menuItemContainerNoClick" : "menuItemContainer"}
							data-name={"context-menu-item-" + item.id}
							style={generatedStyle}
							key={idx++}
							onClick={e =>
							{
								if(item.noclick) return;
								if(item.nested)
								{
									const rect = (e.target as HTMLElement)?.getBoundingClientRect();
									showNestedItems((rect.x + rect.width), rect.y, props.category ?? "Context Menu", undefined, item.nested);
									return;
								}
								if (item.action)
									dispatch(item.action);

								if (item.callback)
								{
									const params =
									item.callbackParams ?
									  Array.isArray(item.callbackParams) ? item.callbackParams
									    : [item.callbackParams]
									  : [];
									item.callback(...params);
								}

								AnalyticsManager.managedEvent('contextMenuClicked', "managedEventData", (e.target as any)?.dataset?.name, props.category ?? "Context Menu");

								if (props.onShutdown)
								{
									props.onShutdown();
								}
							}}
						>
							{ item.icon }
							{item.renderObject}
							<Spacer stretchTo={{ x:"100%" }} />
							{item.shortcut}
							{
								item.nested &&
									<Panel orientation={LayoutOrientations.Horizontal}>
										<Spacer stretchTo={{ x:"100%" }}/>
										<TriangleIconRight />
									</Panel>
							}
						</div>
					)
				}

				output.push(
					<Separator
						key={idx++}
						orientation={LayoutOrientations.Horizontal}
						className={"solid"}
					/>
				);
			}
		}

		// remove last separator
		output.pop();

		return output;
	}

	return (
		<Panel className="dark contextMenuRoot"
			data-name={"context-menu-root"}
			size={props.size}
		>
			{
				generateMenuItems()
			}
		</Panel>
	);
}
